import {
  CTCSS_TONES,
  DTCS_CODES,
  type RadioProgress,
  type WebChannel,
} from './baofeng-web.ts'
import type { RadioTransport } from './web-serial.ts'

const ACK = 0x06
const BLOCK_SIZE = 64
const MEMORY_SIZE = 31561
const CHECKSUM_OFFSET = 0x7b48
const CHANNEL_OFFSET = 0x04c8
const CHANNEL_SIZE = 16
const NAME_OFFSET = 0x4988
const NAME_SIZE = 8
const SKIP_OFFSET = 0x7648
const CHARSET =
  "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ !`o$%&'()*+,-./|;/=>?@[u]^_"

export type FT7800Profile = {
  protocol: 'ft7800'
  id: string
  vendor: 'Yaesu'
  model: string
  summary: string
  tags: readonly string[]
  baudRate: 9600
  channelMinimum: 1
  channelMaximum: 999
  nameLength: 6
  memorySize: number
  powerLevels: readonly string[]
}

export const FT7800_PROFILES: readonly FT7800Profile[] = [
  {
    protocol: 'ft7800',
    id: 'yaesu-ft-7800-7900',
    vendor: 'Yaesu',
    model: 'FT-7800/7900',
    summary: 'Yaesu FT-7800 and FT-7900 VHF/UHF mobiles.',
    tags: ['Experimental', 'Read + write', '999 channels', '6-character names'],
    baudRate: 9600,
    channelMinimum: 1,
    channelMaximum: 999,
    nameLength: 6,
    memorySize: MEMORY_SIZE,
    powerLevels: ['Hi', 'Mid1', 'Mid2', 'Low'],
  },
]

function emptyChannel(number: number, profile: FT7800Profile): WebChannel {
  return {
    number,
    empty: true,
    name: '',
    rxFrequency: null,
    txFrequency: null,
    toneMode: 'none',
    ctcss: null,
    dtcs: null,
    dtcsPolarity: 'N',
    mode: 'FM',
    power: profile.powerLevels[0],
    skip: false,
  }
}

function decodePackedBcd(raw: Uint8Array): number {
  let value = 0
  for (const byte of raw) value = value * 100 + (byte >> 4) * 10 + (byte & 0x0f)
  return value
}

function decodeFrequency(raw: Uint8Array): number | null {
  if (raw.length !== 3 || raw.every((value) => value === 0xff)) return null
  let frequency = decodePackedBcd(raw) * 10_000
  if (frequency > 8_000_000_000) frequency = frequency - 8_000_000_000 + 5000
  if (frequency > 4_000_000_000) frequency = frequency - 4_000_000_000 + 2500
  return frequency
}

function encodeFrequency(frequency: number, target: Uint8Array): void {
  let value = Math.floor(frequency / 10_000)
  for (let index = 2; index >= 0; index -= 1) {
    const part = value % 100
    target[index] = ((Math.floor(part / 10) << 4) | (part % 10)) & 0xff
    value = Math.floor(value / 100)
  }
  if (frequency % 1000 === 500) target[0] |= 0x40
  if (frequency % 10_000 >= 5000) target[0] |= 0x80
}

function decodeName(image: Uint8Array, number: number): string {
  const offset = NAME_OFFSET + (number - 1) * NAME_SIZE
  if ((image[offset + 7] & 0x01) === 0) return ''
  let name = ''
  for (let index = 0; index < 6; index += 1) {
    const code = image[offset + index]
    if (code === 0xff) break
    name += CHARSET[code & 0x7f] ?? ' '
  }
  return name.trimEnd()
}

function encodeName(image: Uint8Array, number: number, value: string): void {
  const offset = NAME_OFFSET + (number - 1) * NAME_SIZE
  const name = value.toUpperCase().slice(0, 6).padEnd(6, ' ')
  for (let index = 0; index < 6; index += 1) {
    const code = CHARSET.indexOf(name[index])
    image[offset + index] = code < 0 ? CHARSET.indexOf(' ') : code
  }
  image[offset + 6] = value ? image[offset + 6] | 0x01 : image[offset + 6] & 0xfe
  image[offset + 7] = value ? image[offset + 7] | 0x01 : image[offset + 7] & 0xfe
}

function decodeSkip(image: Uint8Array, number: number): boolean {
  const index = number - 1
  const value = (image[SKIP_OFFSET + Math.floor(index / 4)] >> ((index & 3) * 2)) & 0x03
  return value !== 0
}

function setSkip(image: Uint8Array, number: number, skip: boolean): void {
  const index = number - 1
  const offset = SKIP_OFFSET + Math.floor(index / 4)
  const shift = (index & 3) * 2
  image[offset] = (image[offset] & ~(0x03 << shift)) | ((skip ? 1 : 0) << shift)
}

export function parseFT7800Channel(
  image: Uint8Array,
  profile: FT7800Profile,
  number: number,
): WebChannel {
  const offset = CHANNEL_OFFSET + (number - 1) * CHANNEL_SIZE
  const raw = image.slice(offset, offset + CHANNEL_SIZE)
  if (raw.byteLength !== CHANNEL_SIZE || (raw[0] & 0x01) === 0) return emptyChannel(number, profile)
  const rxFrequency = decodeFrequency(raw.slice(1, 4))
  if (!rxFrequency) return emptyChannel(number, profile)
  const duplex = (raw[0] >> 5) & 0x07
  const offsetFrequency = (raw[12] * 5) * 10_000
  const txFrequency =
    duplex === 2
      ? rxFrequency - offsetFrequency
      : duplex === 3
        ? rxFrequency + offsetFrequency
        : duplex === 4
          ? decodeFrequency(raw.slice(5, 8))
          : rxFrequency
  const tmode = (raw[4] >> 5) & 0x07
  const toneMode = tmode === 1 || tmode === 2 || tmode === 3 ? 'ctcss' : tmode === 4 ? 'dtcs' : 'none'
  const am = (rxFrequency >= 108_000_000 && rxFrequency < 136_000_000)
  return {
    number,
    empty: false,
    name: decodeName(image, number),
    rxFrequency,
    txFrequency,
    toneMode,
    ctcss: toneMode === 'ctcss' ? CTCSS_TONES[(raw[8] >> 2) & 0x3f] ?? null : null,
    dtcs: toneMode === 'dtcs' ? DTCS_CODES[raw[9] & 0x7f] ?? null : null,
    dtcsPolarity: 'N',
    mode: am ? 'AM' : raw[0] & 0x0c ? (raw[0] & 0x0c) === 0x08 ? 'NFM' : 'FM' : 'FM',
    power: profile.powerLevels[raw[8] & 0x03] ?? profile.powerLevels[0],
    skip: decodeSkip(image, number),
  }
}

export function parseFT7800Channels(image: Uint8Array, profile: FT7800Profile): WebChannel[] {
  return Array.from({ length: 999 }, (_, index) => parseFT7800Channel(image, profile, index + 1))
}

export function applyFT7800Channel(image: Uint8Array, profile: FT7800Profile, channel: WebChannel): void {
  const offset = CHANNEL_OFFSET + (channel.number - 1) * CHANNEL_SIZE
  const raw = image.subarray(offset, offset + CHANNEL_SIZE)
  if (channel.empty) {
    raw[0] &= ~0x01
    return
  }
  if (!channel.rxFrequency) throw new Error('A receive frequency is required.')
  raw[0] |= 0x01
  raw[0] = (raw[0] & 0xf3) | ((channel.mode === 'NFM' ? 2 : channel.mode === 'AM' ? 0 : 1) << 2)
  encodeFrequency(channel.rxFrequency, raw.subarray(1, 4))
  const delta = channel.txFrequency ? channel.txFrequency - channel.rxFrequency : 0
  raw[0] = (raw[0] & 0x1f) | (((delta < 0 ? 2 : delta > 0 ? 3 : 1) & 0x07) << 5)
  if (delta) raw[12] = Math.round(Math.abs(delta) / 10_000 / 5)
  raw[4] = (raw[4] & 0x1f) | ((channel.toneMode === 'dtcs' ? 4 : channel.toneMode === 'ctcss' ? 2 : 0) << 5)
  raw[8] = (raw[8] & 0xfc) | (Math.max(0, profile.powerLevels.indexOf(channel.power)) & 0x03)
  if (channel.toneMode === 'ctcss' && channel.ctcss) {
    const index = CTCSS_TONES.indexOf(channel.ctcss as (typeof CTCSS_TONES)[number])
    raw[8] = (raw[8] & 0x03) | ((index < 0 ? 0 : index) << 2)
  } else if (channel.toneMode === 'dtcs' && channel.dtcs) {
    const index = DTCS_CODES.indexOf(channel.dtcs as (typeof DTCS_CODES)[number])
    raw[9] = (raw[9] & 0x80) | (index < 0 ? 0 : index)
  }
  encodeName(image, channel.number, channel.name)
  setSkip(image, channel.number, channel.skip)
}

function updateChecksum(image: Uint8Array): void {
  let total = 0
  for (let index = 0; index < CHECKSUM_OFFSET; index += 1) total = (total + image[index]) & 0xff
  image[CHECKSUM_OFFSET] = total
}

export async function readFT7800RadioWithTransport(
  profile: FT7800Profile,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<{ image: Uint8Array; reportedModel: string }> {
  await transport.discardInput()
  const header = await transport.readExact(8, 12_000)
  await transport.write(Uint8Array.of(ACK))
  const image = new Uint8Array(MEMORY_SIZE)
  image.set(header, 0)
  const total = Math.floor((MEMORY_SIZE - 1 - 8) / BLOCK_SIZE)
  for (let index = 0; index < total; index += 1) {
    const offset = 8 + index * BLOCK_SIZE
    image.set(await transport.readExact(BLOCK_SIZE, 5000), offset)
    await transport.write(Uint8Array.of(ACK))
    onProgress?.({ current: index + 1, total, label: 'Reading Yaesu FT-7800 memory' })
  }
  image[MEMORY_SIZE - 1] = (await transport.readExact(1, 3000))[0]
  await transport.write(Uint8Array.of(ACK))
  if (image[CHECKSUM_OFFSET] !== image.slice(0, CHECKSUM_OFFSET).reduce((sum, value) => (sum + value) & 0xff, 0)) {
    throw new Error('The downloaded Yaesu image failed its checksum.')
  }
  return { image, reportedModel: profile.model }
}

export async function writeFT7800RadioWithTransport(
  profile: FT7800Profile,
  image: Uint8Array,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<void> {
  if (image.byteLength !== MEMORY_SIZE) throw new Error('The selected image does not match the FT-7800 layout.')
  updateChecksum(image)
  const total = MEMORY_SIZE / BLOCK_SIZE + 1
  for (let index = 0; index < total; index += 1) {
    const offset = index * BLOCK_SIZE
    const block = image.slice(offset, Math.min(image.byteLength, offset + BLOCK_SIZE))
    if (!block.length) break
    await transport.write(block)
    if ((await transport.readExact(1, 3000))[0] !== ACK) throw new Error(`The radio rejected block ${index}.`)
    onProgress?.({ current: index + 1, total, label: 'Writing Yaesu FT-7800 memory' })
  }
}
